import { useEffect, useState } from 'react'
import { api, AuditRunListItem, ProjectListItem } from '../lib/api'
import { Button } from './design-system/Button'
import { Modal } from './design-system/Modal'
import { useLocale } from '../contexts/LocaleContext'
import { statusLabel } from '../lib/i18n'

export function SettingsProjects() {
  const { t, locale } = useLocale()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [runsByProject, setRunsByProject] = useState<Record<string, AuditRunListItem[]>>({})
  const [openId, setOpenId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingProject, setPendingProject] = useState<ProjectListItem | null>(null)
  const [pendingRun, setPendingRun] = useState<AuditRunListItem | null>(null)
  const [confirmName, setConfirmName] = useState('')
  const [busy, setBusy] = useState(false)
  const [canWipe, setCanWipe] = useState(true)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const data = await api.listProjects()
      setProjects(data.projects)
      if (typeof api.getRuntime === 'function') {
        const runtime = await api.getRuntime().catch(() => null)
        if (runtime?.capabilities?.can_wipe === false) setCanWipe(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.noProjects'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function toggleRuns(projectId: string) {
    if (openId === projectId) {
      setOpenId(null)
      return
    }
    setOpenId(projectId)
    if (runsByProject[projectId]) return
    try {
      const data = await api.listAuditRuns({ project_id: projectId, page_size: 100 })
      setRunsByProject((prev) => ({ ...prev, [projectId]: data.audits }))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.noProjects'))
    }
  }

  async function removeProject() {
    if (!pendingProject) return
    setBusy(true)
    setError(null)
    try {
      await api.deleteProject(pendingProject.id, confirmName)
      setPendingProject(null)
      setConfirmName('')
      setOpenId(null)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.deleteProject'))
    } finally {
      setBusy(false)
    }
  }

  async function removeRun() {
    if (!pendingRun) return
    setBusy(true)
    setError(null)
    try {
      await api.deleteAuditRun(pendingRun.id)
      const projectId = pendingRun.project_id
      setPendingRun(null)
      const data = await api.listAuditRuns({ project_id: projectId, page_size: 100 })
      setRunsByProject((prev) => ({ ...prev, [projectId]: data.audits }))
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.deleteRun'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={cardStyle} data-testid="settings-projects">
      <h2 style={h2Style}>{t('settings.projects')}</h2>
      <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)', margin: 0 }}>
        {t('settings.projectsLead')}
      </p>
      {loading && <p className="text-sm">{t('common.loading')}</p>}
      {error && <p className="text-sm" style={{ color: 'var(--reviewer-danger)' }}>{error}</p>}
      {!loading && projects.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>{t('settings.noProjects')}</p>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {projects.map((project) => (
          <li key={project.id} style={rowStyle} data-testid={`settings-project-${project.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{project.display_name}</div>
                <div className="text-xs" style={{ color: 'var(--reviewer-text-secondary)' }}>
                  {project.audit_count} {t('settings.auditsCount')}
                  {project.source_type ? ` · ${project.source_type}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <Button variant="secondary" size="sm" onClick={() => void toggleRuns(project.id)}>
                  {t('settings.runs')}
                </Button>
                {canWipe && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setConfirmName('')
                      setPendingProject(project)
                    }}
                  >
                    {t('settings.deleteProject')}
                  </Button>
                )}
              </div>
            </div>
            {openId === project.id && (
              <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(runsByProject[project.id] || []).length === 0 && (
                  <li className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>{t('reports.none')}</li>
                )}
                {(runsByProject[project.id] || []).map((run) => (
                  <li key={run.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                    <span className="text-sm" style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
                      {run.id.slice(0, 8)} · {statusLabel(locale, run.status)} · {run.total_findings}
                    </span>
                    {canWipe && (
                      <Button variant="secondary" size="sm" onClick={() => setPendingRun(run)}>
                        {t('settings.deleteRun')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      <Modal
        isOpen={Boolean(pendingProject)}
        onClose={() => {
          if (!busy) {
            setPendingProject(null)
            setConfirmName('')
          }
        }}
        title={t('settings.confirmProjectTitle')}
        size="sm"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => { setPendingProject(null); setConfirmName('') }}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              disabled={busy || confirmName.trim() !== (pendingProject?.display_name || '')}
              onClick={() => void removeProject()}
            >
              {t('settings.deleteForever')}
            </Button>
          </div>
        }
      >
        <p className="text-sm">{t('settings.confirmProjectBody')}</p>
        <p className="text-sm" style={{ fontWeight: 600 }}>{pendingProject?.display_name}</p>
        <label className="text-sm" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {t('settings.confirmName')}
          <input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            style={inputStyle}
            autoComplete="off"
            data-testid="settings-confirm-project-name"
          />
        </label>
      </Modal>

      <Modal
        isOpen={Boolean(pendingRun)}
        onClose={() => {
          if (!busy) setPendingRun(null)
        }}
        title={t('settings.confirmRunTitle')}
        size="sm"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPendingRun(null)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void removeRun()}>
              {t('settings.deleteForever')}
            </Button>
          </div>
        }
      >
        <p className="text-sm">{t('settings.confirmRunBody')}</p>
        <p className="text-sm" style={{ fontFamily: 'var(--reviewer-font-mono)' }}>{pendingRun?.id}</p>
      </Modal>
    </section>
  )
}

const h2Style = {
  margin: '0 0 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  fontSize: '16px',
} as const

const cardStyle = {
  padding: '20px',
  border: '1px solid var(--reviewer-border)',
  backgroundColor: 'var(--reviewer-surface)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
}

const rowStyle = {
  padding: '12px',
  border: '1px solid var(--reviewer-border)',
  backgroundColor: 'var(--reviewer-bg)',
}

const inputStyle = {
  padding: '8px 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  backgroundColor: 'var(--reviewer-bg)',
  border: '1px solid var(--reviewer-border)',
  color: 'var(--reviewer-text)',
} as const
